// Shared design tokens + primitives for the /memory Impact & Recent-capture tabs, so the two React
// surfaces read as ONE system. Card chrome was already unified; this unifies the in-card content
// styling (text palette, card-title type, micro-labels, timestamp pill, section header, dividers).
import { Box, Typography } from "@mui/material";

// One palette for both tabs (was two divergent gray families + duplicated teal constants).
export const C = {
  teal: "#2da894",
  tealDeep: "#1f8a78",
  tealSoft: "#e7f5f1",   // pill / soft fill
  rail: "#bfe3dc",       // timeline line
  ink: "#1a1d23",        // card titles / primary
  body: "#3a3f47",       // body claim text
  muted: "#6b727d",      // secondary text, notes, micro-labels
  faint: "#98a0aa",      // least — node ids, placeholders, empty state
  line: "#e4e7eb",       // card border + in-card dashed dividers
  canvas: "#f6f7f9",
};

// Card chrome (already unified across surfaces) — spread onto each card; padding stays per-card.
export const CARD = {
  bgcolor: "#fff",
  border: `1px solid ${C.line}`,
  borderRadius: "8px",
  boxShadow: "0 1px 2px rgba(16,24,40,.04)",
};

// The one uppercase micro-label spec (was three: stat-card label, "organized under", etc.).
export const MICRO_LABEL = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: C.muted,
};

// Two card-headline roles, split by content — bold the SHORT scannable thing, not long prose:
//   CARD_TITLE — a short question headline (Impact receipts): bold reads as a crisp anchor.
//   CARD_LEAD  — a long claim/takeaway lead (Recent capture): medium weight so a full sentence
//                stays readable instead of shouting.
export const CARD_TITLE = {
  color: C.ink,
  fontSize: 14.5,
  fontWeight: 600,
  lineHeight: 1.4,
};
export const CARD_LEAD = {
  color: C.ink,
  fontSize: 14,
  fontWeight: 500,
  lineHeight: 1.45,
};

// Uppercase teal section header (gradient chip + title + subtitle) — shared by both tabs so each
// announces itself the same way.
export function SectionHeader({ title, subtitle }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box component="span" sx={{ flex: "none", width: 11, height: 11, borderRadius: "3px",
               background: `linear-gradient(135deg, ${C.teal}, ${C.tealDeep})` }} />
        <Typography sx={{ fontSize: 12, fontWeight: 800, textTransform: "uppercase",
               letterSpacing: "0.09em", color: C.tealDeep, lineHeight: 1.2 }}>
          {title}
        </Typography>
      </Box>
      {subtitle && (
        <Typography sx={{ fontSize: 12, color: C.muted, mt: 0.75 }}>{subtitle}</Typography>
      )}
    </Box>
  );
}

// Relative-time pill (teal on soft-teal) — the single timestamp treatment across both tabs.
export function TimeChip({ children }) {
  if (!children) return null;
  return (
    <Box component="span" sx={{ flex: "none", fontSize: 11, fontWeight: 600, color: C.tealDeep,
           background: C.tealSoft, borderRadius: "999px", px: 1, py: "2px", whiteSpace: "nowrap",
           mt: "1px" }}>
      {children}
    </Box>
  );
}
